import { useEffect, useState } from "react"
import { BackButton, FooterMark, LogoutForm, Notice } from "../components/Chrome"
import { CloneOverlay } from "../components/CloneOverlay"
import { CloseIcon } from "../components/Icons"
import { HiddenField } from "../lib/rails"
import { RepositoryList } from "../components/RepositoryList"
import { LoadingOrb } from "../components/LoadingOrb"
import { ConceptiveLandingPage } from "../components/ConceptiveLandingPage"


export function RepositoriesPage({ alert, connected, csrfToken, developerBypassEnabled, landingRepository = null, localProjects = null, paths, repositories }) {
  const [clonePending, setClonePending] = useState(false)
  const [showRepositoryHelp, setShowRepositoryHelp] = useState(false)
  const [showSearchModal, setShowSearchModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)

  useEffect(() => {
    if (!showSearchModal) return

    const normalizedQuery = searchQuery.trim()
    if (normalizedQuery.length < 2) {
      setSearchResults([])
      setSearchError(null)
      setSearching(false)
      setHasSearched(false)
      return
    }

    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => {
      setSearching(true)
      setSearchError(null)

      fetch(`${paths.repositorySearch}?q=${encodeURIComponent(normalizedQuery)}`, {
        headers: { Accept: "application/json" },
        signal: controller.signal
      })
        .then(async (response) => {
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}))
            throw new Error(payload.error || "Could not search GitHub right now.")
          }

          return response.json()
        })
        .then((payload) => {
          setSearchResults(Array.isArray(payload) ? payload : [])
          setHasSearched(true)
        })
        .catch((error) => {
          if (error.name === "AbortError") return

          setSearchResults([])
          setSearchError(error.message)
          setHasSearched(true)
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false)
        })
    }, 220)

    return () => {
      controller.abort()
      window.clearTimeout(timeoutId)
    }
  }, [paths.repositorySearch, searchQuery, showSearchModal])

  function handleCloneStart(event) {
    setClonePending(true)
    event.currentTarget.querySelectorAll("button, input").forEach((element) => {
      element.setAttribute("aria-disabled", "true")
    })
  }

  function openSearchModal() {
    setShowSearchModal(true)
    setSearchError(null)
  }

  function closeSearchModal() {
    setShowSearchModal(false)
  }

  function goBackFromRepositoryPicker() {
    window.history.back()
  }

  return (
    <>
      {connected ? (
        <div className={`source-picker-page ${clonePending ? "is-cloning" : ""}`.trim()}>
          <section className="content-stack source-picker-shell" id="repository-intake">
            <div className="repo-step-nav slide-up">
              <BackButton iconOnly label="Back" onClick={goBackFromRepositoryPicker} />
              {developerBypassEnabled ? (
                <LogoutForm
                  action={paths.githubSession}
                  csrfToken={csrfToken}
                  className="welcome-secondary-link repo-step-action"
                  label="Start over"
                />
              ) : null}
            </div>
            <Notice alert={alert} />
            <div className="source-picker-hero slide-up">
              <h1 className="page-title">Choose a repository</h1>
              <div className="source-picker-intro">
                <p className="source-picker-summary">
                  <span>A repository is where your code lives.</span>
                  <button
                    type="button"
                    className="source-picker-more-button repo-subtitle-toggle"
                    onClick={() => setShowRepositoryHelp((current) => !current)}
                  >
                    {showRepositoryHelp ? "Show less" : "Learn more"}
                  </button>
                </p>

                {showRepositoryHelp ? (
                  <div className="source-picker-explainer">
                    A repository is the main project folder for an app, plus the history of every change. Think of it like the blueprint set, parts shelf, and build log for your product all in one place.
                  </div>
                ) : null}
              </div>
            </div>
            <RepositoryList
              repositories={repositories}
              paths={paths}
              csrfToken={csrfToken}
              onCloneStart={handleCloneStart}
            />
            <LocalProjectForm
              csrfToken={csrfToken}
              localProjects={localProjects}
              onSubmit={handleCloneStart}
            />

            <p className="source-picker-search-prompt slide-up" style={{ animationDelay: "0.08s" }}>
              <button type="button" className="source-picker-search-link" onClick={openSearchModal}>
                Search or open a public repository
              </button>
            </p>
          </section>

          <FooterMark />

          {showSearchModal ? (
            <RepositorySearchModal
              csrfToken={csrfToken}
              hasSearched={hasSearched}
              onCloneStart={handleCloneStart}
              onClose={closeSearchModal}
              paths={paths}
              query={searchQuery}
              results={searchResults}
              searchError={searchError}
              searching={searching}
              setQuery={setSearchQuery}
            />
          ) : null}
        </div>
      ) : (
        <ConceptiveLandingPage
          alert={alert}
          developerControls={developerBypassEnabled || localProjects?.enabled ? (
            <>
              <LocalProjectForm
                csrfToken={csrfToken}
                localProjects={localProjects}
                onSubmit={handleCloneStart}
                variant="welcome"
              />
              {developerBypassEnabled ? (
                <form method="post" action={paths.developerGithubSession}>
                  <HiddenField name="authenticity_token" value={csrfToken} />
                  <button type="submit" className="welcome-secondary-link">Developer sign in</button>
                </form>
              ) : null}
            </>
          ) : null}
          landingRepository={landingRepository}
          paths={paths}
        />
      )}

      <CloneOverlay visible={clonePending} />
    </>
  )
}

function LocalProjectForm({ csrfToken, localProjects, onSubmit, variant = "picker" }) {
  if (!localProjects?.enabled || !localProjects?.path) return null

  return (
    <form
      action={localProjects.path}
      method="post"
      className={`local-project-form ${variant === "welcome" ? "is-welcome" : ""}`.trim()}
      onSubmit={onSubmit}
    >
      <HiddenField name="authenticity_token" value={csrfToken} />
      <label className="local-project-field">
        <span>Local project folder</span>
        <input
          type="text"
          name="local_repository[path]"
          placeholder="/Users/you/Codes/app"
          pattern="/.*"
          title="Enter an absolute path starting with /"
          autoComplete="off"
          required
        />
      </label>
      <button type="submit" className="local-project-submit">
        Open local repo
      </button>
    </form>
  )
}

function RepositorySearchModal({
  csrfToken,
  hasSearched,
  onCloneStart,
  onClose,
  paths,
  query,
  results,
  searchError,
  searching,
  setQuery
}) {
  return (
    <div className="search-modal-backdrop" onClick={onClose}>
      <div
        className="search-modal glass slide-up"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repository-search-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="search-modal-header">
          <h2 id="repository-search-title">Search for a repository</h2>
          <button type="button" className="search-modal-close" onClick={onClose} aria-label="Close repository search">
            <CloseIcon />
          </button>
        </div>

        <label className="repository-modal-search">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search or paste a GitHub repository URL"
            aria-label="Search or paste a GitHub repository URL"
            autoFocus
          />
        </label>

        {searchError ? <div className="search-modal-error">{searchError}</div> : null}
        {searching ? (
          <div className="search-modal-status">
            <LoadingOrb state="searching" />
            <span>Searching GitHub…</span>
          </div>
        ) : null}

        {hasSearched ? (
          <RepositoryList
            className="repository-search-results"
            repositories={results}
            paths={paths}
            csrfToken={csrfToken}
            emptyText="No repositories found for that search."
            onCloneStart={onCloneStart}
          />
        ) : (
          <div className="search-modal-empty">
            <div className="empty-title">Search GitHub or paste a public repository URL</div>
          </div>
        )}

        <div className="search-modal-footer">
          <span>Need access?</span>{" "}
          <a href={requestAccessUrl(query)} target="_blank" rel="noopener" className="search-modal-link">
            Request on GitHub
          </a>
        </div>
      </div>
    </div>
  )
}

export function requestAccessUrl(query = "") {
  const normalizedQuery = query.trim()
  if (/^[\w.-]+\/[\w.-]+$/.test(normalizedQuery)) {
    return `https://github.com/${normalizedQuery}`
  }

  return `https://github.com/search?q=${encodeURIComponent(normalizedQuery || "repositories")}&type=repositories`
}
